import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  apiClient,
  ApiError,
  type OrgSkillSummary,
  type TeamDraft,
} from '../lib/api';

interface DiffLine {
  kind: 'context' | 'added' | 'removed';
  text: string;
}

function lineDiff(prev: string, next: string): DiffLine[] {
  const a = prev.split('\n');
  const b = next.split('\n');
  // Longest common subsequence — straightforward DP.
  const dp: number[][] = Array(a.length + 1)
    .fill(null)
    .map(() => Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ kind: 'context', text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ kind: 'removed', text: a[i] });
      i++;
    } else {
      out.push({ kind: 'added', text: b[j] });
      j++;
    }
  }
  while (i < a.length) out.push({ kind: 'removed', text: a[i++] });
  while (j < b.length) out.push({ kind: 'added', text: b[j++] });
  return out;
}

export function Review() {
  const params = useParams();
  const navigate = useNavigate();
  const orgId = decodeURIComponent(params.orgId ?? '');
  const draftId = decodeURIComponent(params.draftId ?? '');

  const [draft, setDraft] = useState<TeamDraft | null>(null);
  const [summary, setSummary] = useState<OrgSkillSummary | null>(null);
  const [canonical, setCanonical] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'approve' | 'send-back'>(null);
  const [sendBackNote, setSendBackNote] = useState('');
  const [showSendBack, setShowSendBack] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await apiClient.orgs.drafts.get(orgId, draftId);
        if (cancelled) return;
        setDraft(d);
        const list = await apiClient.orgs.listSkills(orgId);
        const match = list.skills.find((s) => s.id === d.skillId);
        if (!match) {
          if (!cancelled) setError('Underlying skill no longer exists.');
          return;
        }
        if (cancelled) return;
        setSummary(match);
        const head = await apiClient.orgs.getSkill(orgId, match.skillPath);
        if (cancelled) return;
        setCanonical(head.content);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : 'Failed to load review');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, draftId]);

  const diff = useMemo(() => {
    if (!draft) return [];
    return lineDiff(canonical, draft.body);
  }, [canonical, draft]);

  const handleApprove = async () => {
    setBusy('approve');
    setError(null);
    try {
      await apiClient.orgs.drafts.approve(orgId, draftId);
      navigate(`/orgs/${encodeURIComponent(orgId)}/inbox`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Approve failed');
      setBusy(null);
    }
  };

  const handleSendBack = async () => {
    if (!sendBackNote.trim()) {
      setError('Send-back note is required.');
      return;
    }
    setBusy('send-back');
    setError(null);
    try {
      await apiClient.orgs.drafts.sendBack(orgId, draftId, { note: sendBackNote.trim() });
      navigate(`/orgs/${encodeURIComponent(orgId)}/inbox`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Send-back failed');
      setBusy(null);
    }
  };

  if (!draft) {
    return (
      <main className="flex h-full items-center justify-center text-[12px] text-text-subtle">
        {error ?? 'Loading review…'}
      </main>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <header className="hairline-b flex items-center justify-between gap-3 px-4 py-2">
        <div>
          <Link
            to={`/orgs/${encodeURIComponent(orgId)}/inbox`}
            className="text-[12px] text-text-muted hover:text-[var(--color-accent)]"
          >
            ← Inbox
          </Link>
          <h1 className="mt-1 font-mono text-[13px]">
            Review · {summary?.skillPath ?? draft.skillId}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowSendBack((v) => !v)}
            disabled={busy !== null}
            className="btn"
          >
            Send back
          </button>
          <button
            type="button"
            onClick={handleApprove}
            disabled={busy !== null}
            className="btn btn-primary"
          >
            {busy === 'approve' ? 'Approving…' : 'Approve'}
          </button>
        </div>
      </header>

      {error && (
        <div className="mx-4 mt-2 rounded border bg-danger-soft p-2 text-[11px] text-[var(--color-danger)]">
          {error}
        </div>
      )}

      <div className="mx-4 mt-3 rounded border bg-surface p-3 text-[12px]">
        <div className="text-text-muted">
          <span className="font-semibold">Summary:</span>{' '}
          {draft.summary ?? '(no summary)'}
        </div>
        {draft.note && (
          <div className="mt-1 text-text-muted">
            <span className="font-semibold">Note:</span> {draft.note}
          </div>
        )}
        <div className="mt-1 text-text-subtle">
          By {draft.contributorUserId} ·{' '}
          {draft.submittedAt
            ? new Date(draft.submittedAt).toLocaleString()
            : 'unsubmitted'}
        </div>
      </div>

      {showSendBack && (
        <div className="mx-4 mt-3 rounded border bg-canvas p-3">
          <label className="block text-[11px] text-text-muted">
            Why are you sending it back?
            <textarea
              value={sendBackNote}
              onChange={(e) => setSendBackNote(e.target.value)}
              placeholder="Be specific so the contributor can fix it…"
              className="mt-1 block h-20 w-full resize-none rounded border bg-surface px-2 py-1 font-mono text-[12px] outline-none focus:border-[var(--color-accent)]"
            />
          </label>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowSendBack(false)}
              className="btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSendBack}
              disabled={busy !== null || !sendBackNote.trim()}
              className="btn btn-primary"
            >
              {busy === 'send-back' ? 'Sending…' : 'Send back to contributor'}
            </button>
          </div>
        </div>
      )}

      <pre className="m-4 flex-1 overflow-auto rounded border bg-canvas font-mono text-[12px] leading-relaxed">
        {diff.length === 0 ? (
          <div className="px-4 py-3 text-text-subtle">No changes detected.</div>
        ) : (
          diff.map((line, idx) => (
            <div
              key={idx}
              className={
                line.kind === 'added'
                  ? 'bg-[rgba(0,128,0,0.08)] px-4 py-[1px] text-[#0a7d3a]'
                  : line.kind === 'removed'
                    ? 'bg-[rgba(180,0,0,0.07)] px-4 py-[1px] text-[#a40000]'
                    : 'px-4 py-[1px] text-text-muted'
              }
            >
              <span className="select-none pr-2 text-text-subtle">
                {line.kind === 'added' ? '+' : line.kind === 'removed' ? '−' : ' '}
              </span>
              {line.text || ' '}
            </div>
          ))
        )}
      </pre>
    </div>
  );
}
